import React, { useEffect, useState } from 'react'
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  MenuItem,
  Typography,
  withStyles,
} from '@material-ui/core'
import { Icon } from '@iconify/react'
import {
  get as getCookie,
  set as setCookie,
  remove as removeCookie,
} from 'es-cookie'
import { CoreButton } from 'src/components/core'
import {
  listUserShopsService,
  refreshTokenService,
  switchShopService,
} from 'src/api/authentication'
import { mcpConsentDetailsService } from 'src/api/mcp-connection'
import translator from 'src/translate'
import { getCookieOptions } from 'src/utils/cookie-options'
import {
  getMcpAuthorizeReturnUrl,
  getValidStoredAccessToken,
  parseMcpAuthorizeRequest,
  submitMcpAuthorizeContinue,
} from 'src/utils/mcp-oauth-handoff'

const styles = theme => ({
  root: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    backgroundColor: '#f5f5f5',
    padding: theme.spacing(2),
  },
  card: {
    maxWidth: 480,
    width: '100%',
    padding: theme.spacing(3),
    boxShadow: '0 8px 40px -12px rgba(0,0,0,0.3)',
  },
  logo: {
    height: 60,
    marginBottom: theme.spacing(2),
  },
  section: {
    marginBottom: theme.spacing(2),
  },
  label: {
    fontSize: 12,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    color: '#637381',
    marginBottom: 6,
  },
  destination: {
    display: 'flex',
    alignItems: 'center',
    borderRadius: 8,
    border: '1px solid #FFC107',
    backgroundColor: '#FFF8E1',
    padding: '10px 12px',
  },
  destinationHost: {
    fontSize: 16,
    fontWeight: 700,
    wordBreak: 'break-all',
  },
  unverified: {
    display: 'inline-block',
    fontSize: 11,
    fontWeight: 600,
    color: '#B76E00',
    backgroundColor: '#FFF5CC',
    borderRadius: 4,
    padding: '1px 6px',
    marginLeft: 6,
    verticalAlign: 'middle',
  },
  accessItem: {
    display: 'flex',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  actions: {
    display: 'flex',
    gap: 12,
    marginTop: theme.spacing(1),
  },
})

const workspaceLabel = workspace => `${workspace.shopName}'s Workspace`

/** Plain-words description of each scope the user is asked to grant. */
const ACCESS_DESCRIPTIONS = {
  mcp: () =>
    translator._(
      'Use Mini Course Generator through an AI assistant: read, create and change the courses in this workspace',
    ),
  api: () =>
    translator._(
      'Full API access to this workspace: read and change everything you can, on your behalf',
    ),
  offline_access: () =>
    translator._('Stay connected until you disconnect it, without asking you again'),
}

const ACCESS_ICONS = {
  mcp: 'mdi:robot-outline',
  api: 'mdi:key-variant',
  offline_access: 'mdi:clock-outline',
}

/** What a request without the gateway's description (older gateway) asks for, in display order. */
const accessFromScopes = scopes => {
  const access = []
  if (scopes.includes('api')) access.push('api')
  if (scopes.includes('mcp') || access.length === 0) access.push('mcp')
  if (scopes.includes('offline_access')) access.push('offline_access')
  scopes
    .filter(scope => !['mcp', 'api', 'offline_access', 'openid'].includes(scope))
    .forEach(scope => access.push(scope))
  return access
}

const statusOf = error => error?.response?.status

/**
 * MCP connector / mcg CLI OAuth — the consent screen, between sign-in and the gateway hand-off.
 *
 * Any app can register itself with the gateway (Dynamic Client Registration) and send the user an
 * authorize link, so nothing is connected without the user's explicit Allow here. The screen shows
 * the app's self-declared (unverified) name, where the access will be sent (the redirect host), the
 * workspace, and what the app will be able to do. There is no automatic hand-off, not even for a
 * user with a single workspace.
 *
 * The gateway describes the request (`/authorize/consent`) and issues a single-use consent token that
 * `/authorize/continue` requires. Against an older gateway without that endpoint the screen falls
 * back to what it can read from the authorize URL and still requires the click.
 *
 * Choosing another workspace mints a token for it with SwitchShop `forMcpConnect`, which leaves the
 * user's admin session and default workspace untouched.
 */
const McpConnectPage = ({ classes }) => {
  const [workspaces, setWorkspaces] = useState(null)
  const [selectedShopId, setSelectedShopId] = useState(null)
  const [details, setDetails] = useState(null)
  const [fallback, setFallback] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [fatalError, setFatalError] = useState(null)
  const [denied, setDenied] = useState(false)

  const returnUrl = getMcpAuthorizeReturnUrl()
  // Never let another site frame the consent screen and trick the user into clicking Allow.
  const framed = typeof window !== 'undefined' && window.self !== window.top

  const goToLogin = () => {
    window.location.href = `/login?returnUrl=${encodeURIComponent(returnUrl)}`
  }

  /**
   * The stored admin token was rejected (e.g. superseded by a newer sign-in). Try the refresh token
   * once, like the rest of the admin app does; otherwise sign in again. Returns true when a new token
   * is stored and the caller may retry.
   */
  const recoverSession = async () => {
    const refreshToken = getCookie('refreshToken')
    if (refreshToken) {
      try {
        const response = await refreshTokenService({ refreshToken })
        if (response?.data?.accessToken) {
          setCookie('token', response.data.accessToken, getCookieOptions())
          setCookie('refreshToken', response.data.refreshToken, getCookieOptions())
          setCookie('userId', response.data.userId, getCookieOptions())
          return true
        }
      } catch (refreshError) {
        console.error('Failed to refresh the session for MCP connect', refreshError)
      }
    }
    removeCookie('token')
    goToLogin()
    return false
  }

  /** Runs an authenticated call; on 401 recovers the session once and retries. */
  const withSession = async call => {
    try {
      return await call()
    } catch (callError) {
      if (statusOf(callError) !== 401 || !(await recoverSession())) throw callError
    }
    try {
      return await call()
    } catch (retryError) {
      if (statusOf(retryError) === 401) {
        removeCookie('token')
        goToLogin()
      }
      throw retryError
    }
  }

  const loadDetails = async () => {
    try {
      const response = await withSession(() => mcpConsentDetailsService(returnUrl))
      if (!response?.consentToken) throw new Error('No consent token')
      setDetails(response)
      setFallback(null)
      return response
    } catch (detailsError) {
      const status = statusOf(detailsError)
      if (status === 404 || status === 405) {
        // Older gateway without /authorize/consent: show what the authorize URL says.
        setFallback(parseMcpAuthorizeRequest(returnUrl))
        return null
      }
      if (status !== 401) {
        console.error('Failed to load the MCP authorization request', detailsError)
        const description = detailsError?.response?.data?.error_description
        setFatalError(
          description
            ? `${translator._('This connection request cannot be completed')}: ${description}`
            : translator._('This connection request cannot be completed. Go back to the app you are connecting and try again.'),
        )
      }
      throw detailsError
    }
  }

  useEffect(() => {
    if (!returnUrl) {
      window.location.href = '/'
      return
    }
    if (framed) return
    if (!getValidStoredAccessToken()) {
      goToLogin()
      return
    }

    const load = async () => {
      try {
        await loadDetails()
      } catch {
        return
      }

      try {
        const res = await withSession(() => listUserShopsService())
        const shops = Array.isArray(res?.data) ? res.data : []
        setWorkspaces(shops)
        const current = shops.find(shop => shop.isCurrentShop) || shops[0]
        setSelectedShopId(current ? current.shopId : null)
      } catch (shopsError) {
        // Can't list workspaces: offer the one the user is signed in to.
        console.error('Failed to fetch workspaces', shopsError)
        setWorkspaces([])
      }
    }
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [returnUrl])

  /** The consent token, re-fetched when it is about to expire (the user took a while). */
  const freshConsentToken = async () => {
    if (!details) return undefined
    const expiresAt = Date.parse(details.expiresAt)
    if (Number.isNaN(expiresAt) || expiresAt - Date.now() > 30 * 1000) {
      return details.consentToken
    }
    const reloaded = await loadDetails()
    return reloaded?.consentToken
  }

  const handleAllow = async () => {
    if (!getValidStoredAccessToken()) {
      goToLogin()
      return
    }
    setSubmitting(true)
    setError(null)

    try {
      const selected = (workspaces || []).find(
        workspace => workspace.shopId === selectedShopId,
      )
      let accessToken = getValidStoredAccessToken()
      if (selected && !selected.isCurrentShop) {
        const res = await withSession(() =>
          switchShopService(selectedShopId, { forMcpConnect: true }),
        )
        accessToken = res?.data?.accessToken
        if (!accessToken) throw new Error('No access token for the selected workspace')
      }

      const consentToken = await freshConsentToken()
      if (details && !consentToken) throw new Error('No consent token')
      if (!submitMcpAuthorizeContinue(accessToken, returnUrl, { consentToken, decision: 'allow' })) {
        throw new Error('Could not hand the approval to the gateway')
      }
    } catch (allowError) {
      console.error('Failed to connect the app', allowError)
      setError(translator._('Could not connect to this workspace, please try again'))
      setSubmitting(false)
    }
  }

  const handleDeny = async () => {
    setError(null)
    if (!details) {
      // Older gateway: it cannot pass a denial on to the app, so just stop here.
      setDenied(true)
      return
    }
    setSubmitting(true)
    try {
      const consentToken = await freshConsentToken()
      const accessToken = getValidStoredAccessToken()
      if (
        !consentToken ||
        !submitMcpAuthorizeContinue(accessToken, returnUrl, { consentToken, decision: 'deny' })
      ) {
        setDenied(true)
        setSubmitting(false)
      }
    } catch (denyError) {
      console.error('Failed to send the denial', denyError)
      setDenied(true)
      setSubmitting(false)
    }
  }

  const request = details || fallback
  const loading = !framed && !fatalError && !denied && (!request || !workspaces)
  const access = details ? details.access : accessFromScopes(fallback?.scopes || [])
  const clientName = details
    ? details.clientName || translator._('An unnamed app')
    : translator._('An app')
  const selectedWorkspace = (workspaces || []).find(
    workspace => workspace.shopId === selectedShopId,
  )

  const renderBody = () => {
    if (framed) {
      return (
        <Typography variant="body1" style={{ textAlign: 'center' }}>
          {translator._(
            'For your security, connecting an app has to be done in its own browser window.',
          )}{' '}
          <a href={window.location.href} target="_blank" rel="noopener noreferrer">
            {translator._('Open in a new window')}
          </a>
        </Typography>
      )
    }

    if (fatalError) {
      return (
        <Typography variant="body1" color="error" style={{ textAlign: 'center' }}>
          {fatalError}
        </Typography>
      )
    }

    if (denied) {
      return (
        <Box display="flex" flexDirection="column" alignItems="center">
          <Typography variant="h6" gutterBottom style={{ textAlign: 'center' }}>
            {translator._('Access denied')}
          </Typography>
          <Typography variant="body2" color="textSecondary" style={{ textAlign: 'center' }}>
            {translator._('Nothing was connected. You can close this window.')}
          </Typography>
          <CoreButton
            variant="outlined"
            color="primary"
            onClick={() => {
              window.location.href = '/'
            }}
            style={{ textTransform: 'none', borderRadius: 8, marginTop: 16 }}
          >
            {translator._('Go to Mini Course Generator')}
          </CoreButton>
        </Box>
      )
    }

    if (loading) {
      return (
        <Box display="flex" flexDirection="column" alignItems="center">
          <CircularProgress color="primary" />
          <Typography variant="body1" style={{ marginTop: 16 }}>
            {translator._('Loading...')}
          </Typography>
        </Box>
      )
    }

    return (
      <>
        <Typography variant="h6" gutterBottom style={{ textAlign: 'center' }}>
          <span data-testid="mcp-consent-client-name">{clientName}</span>{' '}
          {translator._('wants to connect to your Mini Course Generator account')}
        </Typography>
        <Typography
          variant="body2"
          color="textSecondary"
          className={classes.section}
          style={{ textAlign: 'center' }}
        >
          <span className={classes.unverified}>{translator._('Unverified')}</span>{' '}
          {details
            ? translator._(
                'The app chose this name itself; Mini Course Generator has not verified it.',
              )
            : translator._('The app did not identify itself.')}{' '}
          {translator._('Only allow it if you started this connection yourself.')}
          {!details && request?.clientId && (
            <>
              {' '}
              {translator._('Client ID')}: <code>{request.clientId}</code>
            </>
          )}
        </Typography>

        <Box className={classes.section}>
          <Typography className={classes.label}>
            {translator._('Access will be sent to')}
          </Typography>
          <Box className={classes.destination}>
            <Icon
              icon="mdi:web"
              color="#B76E00"
              style={{ marginRight: 8, fontSize: 20, flexShrink: 0 }}
            />
            <Box>
              <Typography className={classes.destinationHost} data-testid="mcp-consent-redirect-host">
                {request.redirectHost || request.redirectUri}
              </Typography>
              {request.redirectIsLoopback && (
                <Typography fontSize="12px" color="textSecondary">
                  {translator._('An app running on this computer')}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        <Box className={classes.section}>
          <Typography className={classes.label}>{translator._('Workspace')}</Typography>
          {workspaces.length > 1 ? (
            <Box role="radiogroup">
              {workspaces.map(workspace => {
                const isSelected = workspace.shopId === selectedShopId
                return (
                  <MenuItem
                    key={workspace.shopId}
                    role="radio"
                    aria-checked={isSelected}
                    selected={isSelected}
                    disabled={submitting}
                    onClick={() => setSelectedShopId(workspace.shopId)}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      borderRadius: 6,
                      border: `1px solid ${isSelected ? '#002E47' : '#CBD0D7'}`,
                      marginBottom: 8,
                      paddingTop: 8,
                      paddingBottom: 8,
                    }}
                  >
                    <Box style={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                      <Icon
                        icon="mdi:domain"
                        color="#637381"
                        style={{ marginRight: 8, fontSize: 18, flexShrink: 0 }}
                      />
                      <Box style={{ overflow: 'hidden' }}>
                        <Typography
                          fontSize="14px"
                          fontWeight={isSelected ? 600 : 400}
                          noWrap
                          title={workspaceLabel(workspace)}
                        >
                          {workspaceLabel(workspace)}
                        </Typography>
                        {workspace.roleName && (
                          <Typography fontSize="12px" color="textSecondary" noWrap>
                            {workspace.roleName}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    {isSelected && (
                      <Icon
                        icon="mdi:check"
                        color="#002E47"
                        style={{ fontSize: 18, flexShrink: 0, marginLeft: 8 }}
                      />
                    )}
                  </MenuItem>
                )
              })}
            </Box>
          ) : (
            <Box style={{ display: 'flex', alignItems: 'center' }}>
              <Icon
                icon="mdi:domain"
                color="#637381"
                style={{ marginRight: 8, fontSize: 18, flexShrink: 0 }}
              />
              <Typography fontSize="14px" fontWeight={600}>
                {selectedWorkspace
                  ? workspaceLabel(selectedWorkspace)
                  : translator._('Your current workspace')}
              </Typography>
            </Box>
          )}
        </Box>

        <Box className={classes.section}>
          <Typography className={classes.label}>
            {translator._('It will be able to')}
          </Typography>
          {access.map(scope => (
            <Box key={scope} className={classes.accessItem} data-testid={`mcp-consent-access-${scope}`}>
              <Icon
                icon={ACCESS_ICONS[scope] || 'mdi:shield-key-outline'}
                color={scope === 'api' ? '#B72136' : '#637381'}
                style={{ marginRight: 8, fontSize: 18, flexShrink: 0, marginTop: 2 }}
              />
              <Typography fontSize="14px" fontWeight={scope === 'api' ? 600 : 400}>
                {ACCESS_DESCRIPTIONS[scope] ? ACCESS_DESCRIPTIONS[scope]() : scope}
              </Typography>
            </Box>
          ))}
        </Box>

        {error && (
          <Typography variant="body2" color="error" style={{ marginBottom: 12 }}>
            {error}
          </Typography>
        )}

        <Box className={classes.actions}>
          <CoreButton
            variant="outlined"
            color="primary"
            fullWidth
            disabled={submitting}
            onClick={handleDeny}
            style={{ textTransform: 'none', borderRadius: 8, fontWeight: 600 }}
          >
            {translator._('Deny')}
          </CoreButton>
          <CoreButton
            variant="contained"
            color="primary"
            fullWidth
            disabled={submitting || (workspaces.length > 1 && !selectedShopId)}
            onClick={handleAllow}
            style={{ textTransform: 'none', borderRadius: 8, fontWeight: 600 }}
          >
            {submitting ? translator._('Connecting...') : translator._('Allow')}
          </CoreButton>
        </Box>
      </>
    )
  }

  return (
    <div className={classes.root}>
      <Card className={classes.card}>
        <CardContent>
          <Box display="flex" flexDirection="column" alignItems="center">
            <img src="/assets/logo.png" alt="MCG Logo" className={classes.logo} />
          </Box>
          {renderBody()}
        </CardContent>
      </Card>
    </div>
  )
}

export default withStyles(styles)(McpConnectPage)
